// Round 10 wave B — parse an admin-pasted Rugby League Project player
// reference (R30). Pure, no network.

export type RlpPlayerRefResult =
  | { ok: true; ref: string }
  | { ok: false; reason: "slug_link" | "not_rlp_link" };

const DIGITS = /^[0-9]+$/;

/**
 * Accepts a bare RLP numeric player id, or an RLP player URL that carries one
 * (`/players/<digits>`, with or without scheme, `www`, a trailing slash, or a
 * `/summary.html` suffix). A slug-style player URL (`/players/<name-slug>/…`)
 * is distinguished from any other non-matching input so the admin gets
 * pointed at copying the numeric link instead. A numeric id followed by any
 * other extra path segment is rejected outright (R30) — `/summary.html` is
 * the only recognised suffix.
 */
export function parseRlpPlayerRef(input: string): RlpPlayerRefResult {
  const trimmed = String(input ?? "").trim();
  if (DIGITS.test(trimmed)) return { ok: true, ref: trimmed };

  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return { ok: false, reason: "not_rlp_link" };
  }

  const host = url.hostname.replace(/^www\./i, "").toLowerCase();
  if (host !== "rugbyleagueproject.org") return { ok: false, reason: "not_rlp_link" };

  const segments = url.pathname.split("/").filter(Boolean);
  if (segments[0] !== "players" || segments.length < 2) return { ok: false, reason: "not_rlp_link" };

  const second = segments[1];
  if (!DIGITS.test(second)) return { ok: false, reason: "slug_link" };
  const extra = segments.slice(2);
  if (extra.length > 0 && !(extra.length === 1 && extra[0].toLowerCase() === "summary.html")) {
    return { ok: false, reason: "not_rlp_link" };
  }
  return { ok: true, ref: second };
}
